/**
 * React hook wrapping the OT engine for real-time collaborative editing.
 *
 * Manages:
 *   - OT engine lifecycle per file (created once per fileId, NOT on content changes)
 *   - Firestore subscriptions (operations log via onSnapshot)
 *   - Pending op submission
 *   - Fallback detection (if ops stay pending >10s)
 */

import { useState, useEffect, useRef, useCallback } from 'react';
import {
  collection,
  doc,
  setDoc,
  query,
  where,
  orderBy,
  onSnapshot,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../services/firebase';
import { OTEngine, OTEngineCallbacks } from '../services/otEngine';
import {
  OTComponents,
  OTOperation,
  OTSyncStatus,
  OTClientStateType,
} from '../types/ot';

interface UseOTSyncOptions {
  projectId: string | undefined;
  fileId: string | undefined;
  userId: string | undefined;
  /** Content when the file was first loaded (must NOT change on every keystroke) */
  initialContent: string;
  /** Version when the file was first loaded */
  initialVersion: number;
  enabled: boolean;
  onRemoteOp?: (op: OTComponents, userId: string) => void;
}

interface UseOTSyncResult {
  /** Apply a local edit operation */
  applyLocal: (op: OTComponents) => void;
  /** Current OT engine state */
  engineState: OTClientStateType;
  /** Connection/sync status */
  syncStatus: OTSyncStatus;
  /** Whether the engine is fully synchronized */
  isSynchronized: boolean;
  /** Last error message */
  error: string | null;
  /** Get the engine's current document (for state sync after remote ops) */
  getDocument: () => string;
  /** Latest committed version seen by this engine */
  currentVersion: number;
}

const FALLBACK_TIMEOUT_MS = 10_000;
/** Set to true to enable verbose OT console logging */
const OT_DEBUG = false;

export function useOTSync({
  projectId,
  fileId,
  userId,
  initialContent,
  initialVersion,
  enabled,
  onRemoteOp,
}: UseOTSyncOptions): UseOTSyncResult {
  const [engineState, setEngineState] = useState<OTClientStateType>('synchronized');
  const [syncStatus, setSyncStatus] = useState<OTSyncStatus>('connecting');
  const [error, setError] = useState<string | null>(null);
  const [currentVersion, setCurrentVersion] = useState(initialVersion);

  const engineRef = useRef<OTEngine | null>(null);
  const fallbackTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const onRemoteOpRef = useRef(onRemoteOp);

  // Capture initial content/version in refs so they don't cause re-creation.
  // These only update when fileId changes (via the effect below).
  const initialContentRef = useRef(initialContent);
  const initialVersionRef = useRef(initialVersion);

  // Keep callback ref current
  onRemoteOpRef.current = onRemoteOp;

  // Update initial refs when fileId changes
  useEffect(() => {
    initialContentRef.current = initialContent;
    initialVersionRef.current = initialVersion;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [fileId]);

  // Clean up fallback timer
  const clearFallbackTimer = useCallback(() => {
    if (fallbackTimerRef.current) {
      clearTimeout(fallbackTimerRef.current);
      fallbackTimerRef.current = null;
    }
  }, []);

  // Start fallback timer when an op is sent
  const startFallbackTimer = useCallback(() => {
    clearFallbackTimer();
    fallbackTimerRef.current = setTimeout(() => {
      setSyncStatus('fallback');
      setError('Real-time sync unavailable — falling back to manual save');
    }, FALLBACK_TIMEOUT_MS);
  }, [clearFallbackTimer]);

  // Main effect: create engine and subscribe to operations.
  // Only depends on identity values (projectId, fileId, userId, enabled) — NOT content.
  useEffect(() => {
    if (!enabled || !projectId || !fileId || !userId) {
      setSyncStatus('disconnected');
      return;
    }

    setError(null);
    setSyncStatus('connecting');

    const content = initialContentRef.current;
    const version = initialVersionRef.current;
    setCurrentVersion(version);

    OT_DEBUG && console.log(`[OT] Engine created for file=${fileId}, version=${version}, contentLen=${content.length}`);

    // Track pending op IDs written by THIS tab for accurate ack detection.
    // Using a Set of Firestore doc IDs avoids the multi-tab userId collision bug.
    const pendingOpIds = new Set<string>();

    // Create OT engine
    const callbacks: OTEngineCallbacks = {
      sendOp(op: OTComponents, baseVersion: number) {
        OT_DEBUG && console.log(`[OT] Sending op to pendingOps, baseVersion=${baseVersion}, op=`, op);
        const pendingOpsCol = collection(
          db,
          'projects', projectId,
          'files', fileId,
          'pendingOps'
        );
        // Pre-generate a doc reference so we know the ID before writing.
        // The server copies this ID into the committed op as `clientId`.
        const docRef = doc(pendingOpsCol);
        pendingOpIds.add(docRef.id);

        setDoc(docRef, {
          userId,
          baseVersion,
          components: op,
          clientTimestamp: Timestamp.now(),
          status: 'pending',
        }).then(() => {
          OT_DEBUG && console.log(`[OT] pendingOp written successfully, id=${docRef.id}`);
        }).catch((err) => {
          console.error(`[OT] Failed to write pendingOp:`, err);
          pendingOpIds.delete(docRef.id);
          setError(`Failed to send operation: ${err.message}`);
        });

        startFallbackTimer();
      },

      onEvent(event) {
        switch (event.type) {
          case 'stateChanged':
            OT_DEBUG && console.log(`[OT] Engine state → ${event.state}`);
            setEngineState(event.state);
            break;
          case 'remoteOp':
            OT_DEBUG && console.log(`[OT] Remote op received from user=${event.userId}, op=`, event.op);
            onRemoteOpRef.current?.(event.op, event.userId);
            break;
          case 'error':
            console.error(`[OT] Engine error:`, event.error);
            setError(event.error);
            break;
          // 'documentChanged' is intentionally NOT forwarded to React state.
          // Remote content updates go through pushEditOperations in the editor,
          // and state is synced via getDocument() after application.
        }
      },
    };

    const engine = new OTEngine(content, version, callbacks);
    engineRef.current = engine;

    // Subscribe to committed operations via onSnapshot
    const opsQuery = query(
      collection(db, 'projects', projectId, 'files', fileId, 'operations'),
      where('version', '>', version),
      orderBy('version', 'asc')
    );

    const unsub = onSnapshot(
      opsQuery,
      (snapshot) => {
        setSyncStatus('connected');

        const changes = snapshot.docChanges().filter(c => c.type === 'added');
        if (changes.length > 0) {
          OT_DEBUG && console.log(`[OT] onSnapshot: ${changes.length} new operation(s) received`);
        }

        for (const change of changes) {
          const data = change.doc.data();
          const serverOp: OTOperation = {
            id: data.id,
            userId: data.userId,
            baseVersion: data.baseVersion,
            version: data.version,
            components: data.components,
            clientId: data.clientId,
            serverTimestamp: data.serverTimestamp?.toDate() ?? new Date(),
            clientTimestamp: data.clientTimestamp?.toDate() ?? new Date(),
          };

          // Match acks by clientId (pending op doc ID), NOT userId.
          // This prevents multi-tab conflicts where the same user's ops
          // from another tab would be incorrectly treated as acks.
          if (serverOp.clientId && pendingOpIds.has(serverOp.clientId)) {
            OT_DEBUG && console.log(`[OT] Own op ack'd, version=${serverOp.version}, clientId=${serverOp.clientId}`);
            pendingOpIds.delete(serverOp.clientId);
            engine.handleAck(serverOp.version);
            clearFallbackTimer();
          } else {
            OT_DEBUG && console.log(`[OT] Remote op from user=${serverOp.userId}, version=${serverOp.version}`);
            engine.handleRemoteOp(serverOp);
          }
          setCurrentVersion(serverOp.version);
        }
      },
      (err) => {
        console.error('[OT] Subscription error:', err);
        setSyncStatus('disconnected');
        setError(`Sync disconnected: ${err.message}`);
      }
    );

    return () => {
      OT_DEBUG && console.log(`[OT] Engine destroyed for file=${fileId}`);
      unsub();
      engine.destroy();
      engineRef.current = null;
      clearFallbackTimer();
    };
  }, [projectId, fileId, userId, enabled, startFallbackTimer, clearFallbackTimer]);

  const applyLocal = useCallback((op: OTComponents) => {
    engineRef.current?.applyLocal(op);
  }, []);

  const getDocument = useCallback(() => {
    return engineRef.current?.getDocument() ?? '';
  }, []);

  return {
    applyLocal,
    engineState,
    syncStatus,
    isSynchronized: engineState === 'synchronized',
    error,
    getDocument,
    currentVersion,
  };
}
